import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const AI_MODELS = [
  { name: 'OpenAI GPT-4o', tag: 'Most capable', desc: 'Industry-leading reasoning and writing. Powers most of Claw by default.', icon: '🧠', color: '#10A37F' },
  { name: 'Claude (Anthropic)', tag: 'Most nuanced', desc: 'Exceptionally thoughtful, long-context conversations. Great for complex tasks.', icon: '🌀', color: '#7B5EA7' },
  { name: 'Gemini (Google)', tag: 'Google ecosystem', desc: "Deep integration with Gmail, Drive, and Google Calendar. Ideal if you're in the Google suite.", icon: '✨', color: '#4285F4' },
  { name: 'Your own API key', tag: 'Bring your own', desc: 'Already paying for an AI subscription? Connect it to Claw and cut out the middleman.', icon: '🔑', color: '#C17B4A' },
];

const ModelCard = ({ model, index }) => (
  <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.08 }}
    className="flex items-start gap-3 p-3.5 bg-white border border-[#E8E2DA] rounded-xl hover:border-[#B8CDD9] transition-colors">
    <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 text-lg"
      style={{ background: `${model.color}18`, border: `1px solid ${model.color}30` }}>
      {model.icon}
    </div>
    <div className="flex-grow min-w-0">
      <div className="flex items-center gap-2 flex-wrap">
        <p className="text-[13px] font-bold text-[#1A1A18]">{model.name}</p>
        <span className="text-[9px] font-bold px-2 py-0.5 rounded-full font-mono-custom uppercase tracking-wider"
          style={{ background: `${model.color}18`, color: model.color }}>
          {model.tag}
        </span>
      </div>
      <p className="text-[11px] text-[#6B6560] leading-relaxed mt-0.5">{model.desc}</p>
    </div>
  </motion.div>
);

const AIDisclaimer = () => {
  const [expanded, setExpanded] = useState(false);

  return (
    <section className="py-16 bg-[#F8F6F3] border-t border-[#E8E2DA]">
      <div className="max-w-3xl mx-auto px-6 lg:px-8">
        <motion.div initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
          className="bg-white border border-[#E8E2DA] rounded-2xl overflow-hidden shadow-sm">

          {/* Header */}
          <div className="px-7 py-5 border-b border-[#E8E2DA] flex items-start justify-between gap-4">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-xl bg-[#EBF2F9] border border-[#C8DDE9] flex items-center justify-center flex-shrink-0 text-lg mt-0.5">
                ⚙️
              </div>
              <div>
                <p className="font-mono-custom text-[9px] tracking-[0.25em] text-[#2D6A8F] uppercase font-semibold mb-1">How Claw works under the hood</p>
                <h3 className="text-[17px] font-extrabold text-[#1A1A18] leading-snug tracking-tight">
                  Claw runs on an AI model — think of it like the engine in a car.
                </h3>
              </div>
            </div>
          </div>

          {/* Body */}
          <div className="px-7 py-5">
            <p className="text-[14px] text-[#6B6560] leading-relaxed mb-4">
              Claw is the <strong className="text-[#1A1A18]">intelligent layer on top</strong> — it learns your preferences, manages your tools, and takes action on your behalf. But like any car needs fuel, Claw needs an AI model to think and respond.
            </p>

            <div className="flex items-start gap-3 p-4 bg-[#FBF4E8] border border-[#DFC98A] rounded-xl mb-4">
              <span className="text-xl flex-shrink-0 mt-0.5">⛽</span>
              <div>
                <p className="text-[13px] font-bold text-[#8A6A2A] mb-1">AI model = the fuel. Claw = the car.</p>
                <p className="text-[12px] text-[#8A6A2A] leading-relaxed">
                  You don't need to think about this day-to-day. We set everything up for you — and if you already have an AI subscription (like ChatGPT Plus), we can connect it directly so you're not paying twice.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3 p-4 bg-[#EBF2F9] border border-[#C8DDE9] rounded-xl mb-5">
              <span className="text-xl flex-shrink-0 mt-0.5">🔌</span>
              <div>
                <p className="text-[13px] font-bold text-[#2D6A8F] mb-1">Claw can hook up to your active subscriptions.</p>
                <p className="text-[12px] text-[#2D6A8F] leading-relaxed">
                  If you're already paying for Gmail, Outlook, Notion, Calendly, or an AI tool — Claw integrates directly. One assistant. All your apps. No extra logins.
                </p>
              </div>
            </div>

            <button onClick={() => setExpanded(!expanded)}
              className="flex items-center gap-2 text-[13px] font-semibold text-[#2D6A8F] hover:text-[#1E4D7A] transition-colors mb-1">
              <SafeIcon icon={expanded ? FiIcons.FiChevronUp : FiIcons.FiChevronDown} className="text-sm" />
              {expanded ? 'Hide AI model options' : 'See recommended AI models for Claw'}
            </button>

            <AnimatePresence>
              {expanded && (
                <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.3 }} className="overflow-hidden">
                  <p className="text-[12px] text-[#9E9690] mb-3 mt-2">
                    We recommend one of these models based on your needs. Don't worry — we handle the setup completely.
                  </p>
                  <div className="space-y-2">
                    {AI_MODELS.map((model, i) => <ModelCard key={model.name} model={model} index={i} />)}
                  </div>
                  <p className="text-[11px] text-[#9E9690] mt-3 leading-relaxed">
                    Not sure which to pick? During your onboarding call, we'll recommend the best fit based on how you work — and set it up for you.
                  </p>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Footer */}
          <div className="px-7 py-4 bg-[#F8F6F3] border-t border-[#E8E2DA] flex items-center gap-2">
            <SafeIcon icon={FiIcons.FiShield} className="text-[#3D7A5E] text-sm flex-shrink-0" />
            <p className="text-[11px] text-[#6B6560] leading-relaxed">
              Your data is never used to train AI models. Claw runs on dedicated infrastructure — private to you.
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default AIDisclaimer;